/**
 * Citation density — sentence-level coverage check (CT pack, CT6).
 *
 * Counts the share of sentences in a rendered report that carry at
 * least one citation source. The LLM emits a structured `claims[]`
 * field alongside the narrative (CT6 schema bump). Each entry has
 *
 *   { text: '<single sentence>', sources: ['ref:3', 'doc:abc', 'sed:site.acres'] }
 *
 * Density = cited sentences / total sentences. Reports below a tunable
 * floor (default 80%) flag in the Citation Quality KPI so the admin
 * dashboard (CT9, deferred) can surface drift before customers see it.
 *
 * Back-compat: prose-only legacy reports without a `claims[]` field
 * still flow through `computeCitationDensity` — the function falls back
 * to scanning the narrative for `[Ref N]` tokens (the historical
 * citation primitive). The density value is comparable across both
 * shapes so admin telemetry doesn't need to special-case generations.
 *
 * Pure module. No React, no Firestore, no Gemini. Sentence splitting
 * is deterministic so the same narrative produces the same density on
 * every invocation — important for invariant tests + rerun-diff.
 */
import type { ReportClaim } from '../types/citation';

/** The default density floor. 80% of sentences must cite ≥ 1 source.
 *  Configurable per tenant in admin (CT9) — callers may override via
 *  the `floor` argument. */
export const DEFAULT_CITATION_DENSITY_FLOOR = 0.8;

/** A computed density record persisted on `SiteRagReport.citationDensity`. */
export interface CitationDensityResult {
  /** Total sentences counted across the narrative. */
  totalSentences: number;
  /** Sentences with at least one source attachment. */
  citedSentences: number;
  /** `citedSentences / totalSentences`, or 1 when `totalSentences === 0`
   *  (an empty narrative is trivially "covered"). Always 0..1. */
  density: number;
  /** The floor used for the `belowFloor` decision. */
  floor: number;
  /** True when `density < floor`. Quality KPI flag. */
  belowFloor: boolean;
  /** Where the density measurement came from. `'claims'` means CT6
   *  structured claims[]; `'prose'` means legacy `[Ref N]` scan. Lets
   *  admin telemetry segment quality by generation shape. */
  source: 'claims' | 'prose';
}

/* ── Sentence splitter ───────────────────────────────────────────────── */

/**
 * Conservative sentence splitter. Splits on `. ! ?` followed by
 * whitespace or end-of-string. Keeps the punctuation on the preceding
 * sentence so a downstream caller wanting to render footnote markers
 * after the period gets the right offset.
 *
 * Not perfect — "Dr. Smith" and "U.S." would split inside the name —
 * but reports rarely embed those, and the density metric is a coarse
 * KPI not a typographical engine. False splits inflate `totalSentences`
 * by ~1-2% which is well within the floor's noise band.
 *
 * Empty / whitespace-only fragments are dropped.
 */
export function splitSentences(text: string | null | undefined): string[] {
  if (typeof text !== 'string' || !text) return [];
  const trimmed = text.trim();
  if (!trimmed) return [];
  // Split on sentence-ending punctuation followed by whitespace.
  // The lookbehind keeps `.`/`!`/`?` on the preceding fragment.
  const parts = trimmed.split(/(?<=[.!?])\s+/);
  return parts.map(p => p.trim()).filter(p => p.length > 0);
}

/** Returns true when a sentence (or its claim entry) carries at least
 *  one citation source. Used by both the prose and claims paths. */
const REF_TOKEN_PROBE = /\[Ref \d+\]/;
const RELIABILITY_TOKEN_PROBE = /\[(verified|inferred|unverified)(?:\s+[^\]]+)?\]/i;

export function sentenceHasProseCitation(sentence: string): boolean {
  return REF_TOKEN_PROBE.test(sentence) || RELIABILITY_TOKEN_PROBE.test(sentence);
}

/* ── Density computers ───────────────────────────────────────────────── */

/**
 * Compute density from a structured `claims[]` array. Each claim's
 * `text` counts as ONE sentence — the LLM is instructed to emit one
 * sentence per claim entry (CT6 prompt rules). A claim with a non-empty
 * `sources` array is "cited"; an empty sources array (or a missing
 * field) is uncited.
 */
export function computeClaimsDensity(
  claims: ReportClaim[],
  floor: number = DEFAULT_CITATION_DENSITY_FLOOR,
): CitationDensityResult {
  const totalSentences = claims.length;
  let citedSentences = 0;
  for (const c of claims) {
    if (c && Array.isArray(c.sources) && c.sources.length > 0) {
      citedSentences += 1;
    }
  }
  const density = totalSentences > 0 ? citedSentences / totalSentences : 1;
  const f = clampFloor(floor);
  return {
    totalSentences,
    citedSentences,
    density,
    floor: f,
    belowFloor: totalSentences > 0 && density < f,
    source: 'claims',
  };
}

/**
 * Compute density from prose-only legacy text (no structured claims[]).
 * Splits the joined narrative into sentences and counts those carrying
 * at least one `[Ref N]` or reliability token. Used as the fallback
 * shape for reports generated before CT6 shipped.
 *
 * `texts` is a list of free-form narrative strings — the caller joins
 * the per-section bodies they want included in the KPI. Passing the
 * full executiveSummary + sitingNotes + each grid/transport/etc.
 * paragraph mirrors what the rendered report shows the user.
 */
export function computeProseDensity(
  texts: Array<string | null | undefined>,
  floor: number = DEFAULT_CITATION_DENSITY_FLOOR,
): CitationDensityResult {
  const sentences: string[] = [];
  for (const t of texts) {
    sentences.push(...splitSentences(t));
  }
  const totalSentences = sentences.length;
  let citedSentences = 0;
  for (const s of sentences) {
    if (sentenceHasProseCitation(s)) citedSentences += 1;
  }
  const density = totalSentences > 0 ? citedSentences / totalSentences : 1;
  const f = clampFloor(floor);
  return {
    totalSentences,
    citedSentences,
    density,
    floor: f,
    belowFloor: totalSentences > 0 && density < f,
    source: 'prose',
  };
}

/**
 * Top-level entry: prefer the structured `claims[]` path when the
 * report carries it; fall back to scanning prose for `[Ref N]` tokens.
 * Both branches return the same shape so callers (KPI badge, admin
 * dashboard, invariant tests) handle them uniformly.
 *
 * `floor` defaults to `DEFAULT_CITATION_DENSITY_FLOOR`. Tenants can
 * supply their own floor via the admin override (CT9, deferred to
 * stage d) — the override resolves before the call site so this
 * function never reads tenant state.
 */
export function computeCitationDensity(
  input: {
    claims?: ReportClaim[] | null;
    proseTexts?: Array<string | null | undefined>;
  },
  floor: number = DEFAULT_CITATION_DENSITY_FLOOR,
): CitationDensityResult {
  if (input.claims && input.claims.length > 0) {
    return computeClaimsDensity(input.claims, floor);
  }
  return computeProseDensity(input.proseTexts ?? [], floor);
}

function clampFloor(x: number): number {
  if (!Number.isFinite(x)) return DEFAULT_CITATION_DENSITY_FLOOR;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}
